import React, {
  FC,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { useRecruiter } from '../../page-components/Recruiiter/RecruiterContext';
import { useConnection } from '../../connection/Application';
import { Modal, Spin } from 'antd';
import Utils from '../../utils/Utils';
import Router from 'next/router';

export type SubscriptionData = {
  status: 'UP' | 'DOWN' | 'SAME' | 'AUTO' | 'NO_CHANGE' | 'FREE_TRIAL';
  currentPackage: any;
  nextPackage: any;
  newBilling: {
    amount: number;
    additionalDays: number;
    price: number;
    endDate: number;
    discount: number;
    cycle: string;
    cycleStart: string;
    remainingDays: number;
    totalDays: number;
    prevTotalDays: number;
  };
};

export type SubscriptionChangeModalInstance = {
  show: (subscriptionId: string) => void;
};

const NoAction: FC<{ data: SubscriptionData }> = ({ data }) => {
  return (
    <div>
      Your current <em>{data.currentPackage.fullName}</em> subscription plan will be automatically
      changed to <em>{data.nextPackage.fullName}</em> at the beginning of the next billing cycle on{' '}
      <em>{data.newBilling.cycleStart}</em>.
    </div>
  );
};

const DowngradeSubscription: FC<{ data: SubscriptionData }> = ({ data }) => {
  return (
    <div className="flex flex-col items-center">
      <p className="text-center">
        {`You're about to downgrade your plan from `}
        <strong>{data.currentPackage.fullName}</strong> to{' '}
        <strong>{data.nextPackage.fullName}</strong>
      </p>
      {data.nextPackage.price > 0 ? (
        <>
          <p className="text-center">
            You will be downgraded to the new subscription plan and will be charged with the total
            amount of the new downgraded plan at the beginning of the next billing cycle.
          </p>
          <div className="w-full px-10">
            <div>
              <strong>New Billing Amount</strong>
            </div>
            <div className="pl-4">
              LKR {Utils.formatNumber(data.nextPackage.price, 2)} (Billed {data.nextPackage.billing}
              )
            </div>
          </div>
          <div className="w-full px-10 mt-4">
            <div>
              <strong>Billing Period</strong>
            </div>
            <div className="pl-4">{data.newBilling.cycle}</div>
          </div>
        </>
      ) : (
        <p className="text-center">
          You will be downgraded to the new subscription plan at the beginning of the next billing
          cycle.
        </p>
      )}
    </div>
  );
};

const UpgradeSubscriptionWithoutDiscount: FC<{ data: SubscriptionData }> = ({ data }) => {
  return (
    <div className="flex flex-col items-center">
      <p className="text-center">
        {`You're about to upgrade your plan from `}
        <strong>{data.currentPackage.fullName}</strong> to{' '}
        <strong>{data.nextPackage.fullName}</strong>
      </p>
      <p className="text-center">
        You will be upgraded to the new subscription plan instantly and will be charged with the
        total amount of the new upgraded plan.
      </p>
      <div className="w-full px-10">
        <div>
          <strong>New Billing Amount</strong>
        </div>
        <div className="pl-4">
          LKR {Utils.formatNumber(data.newBilling.price, 2)} (Billed {data.nextPackage.billing})
        </div>
      </div>
      <div className="w-full px-10 mt-4">
        <div>
          <strong>Billing Period</strong>
        </div>
        <div className="pl-4">{data.newBilling.cycle}</div>
      </div>
    </div>
  );
};

const UpgradeSubscription: FC<{ data: SubscriptionData }> = ({ data }) => {
  if (data.newBilling.discount === 0) {
    return <UpgradeSubscriptionWithoutDiscount data={data} />;
  }
  return (
    <div className="flex flex-col items-center">
      <p className="text-center">
        {`You're about to upgrade your plan from `}
        <strong>{data.currentPackage.fullName}</strong> to{' '}
        <strong>{data.nextPackage.fullName}</strong>
      </p>
      {data.newBilling.discount > 0 ? (
        <p className="text-center">
          The <strong>{data.currentPackage.fullName}</strong> plan subscription fee for the
          remaining usage will be deducted from the <strong>{data.nextPackage.fullName}</strong>{' '}
          plan subscription fee and only the prorated total due for the upgrade will be charged.
        </p>
      ) : (
        <p className="text-center">
          You will be upgraded to the new subscription plan instantly and will be charged with the
          total amount of the new upgraded plan.
        </p>
      )}
      {data.newBilling.discount > 0 ? (
        <>
          <table className="border border-solid">
            <tbody>
              <tr>
                <td className="px-8 w-1/2 align-top py-2">
                  {data.newBilling.discount > 0 ? (
                    <strong>{data.nextPackage.fullName} plan subscription fee</strong>
                  ) : (
                    <strong>New Billing Amount</strong>
                  )}
                </td>
                <td className="align-top py-2"> =</td>
                <td className="w-1/2 align-top py-2">
                  <strong>LKR {Utils.formatNumber(data.newBilling.price, 2)}</strong> (Billed{' '}
                  {data.nextPackage.billing})
                </td>
              </tr>
              <tr className="pb-8">
                <td className="px-8 w-1/2 align-top py-2">
                  <strong>Discount</strong>
                  <br />
                  {data.currentPackage.fullName} Subscription Fee ×<br /> (Days left in subscription
                  term / subscription term)
                </td>
                <td className="align-top py-2"> =</td>
                <td className="w-1/2 align-top py-2">
                  <strong>LKR {Utils.formatNumber(data.newBilling.discount, 2)}</strong>
                  &nbsp;&nbsp;(LKR {Utils.formatNumber(data.currentPackage.price, 0)} × (
                  {data.newBilling.remainingDays} / {data.newBilling.prevTotalDays}))
                </td>
              </tr>
              {data.newBilling.additionalDays > 0 && (
                <tr>
                  <td className="px-8 w-1/2 align-top py-2">
                    <strong>Remaining Amount</strong>
                  </td>
                  <td className="align-top py-2"> =</td>
                  <td className="w-1/2 align-top py-2">
                    <strong>
                      LKR {Utils.formatNumber(data.newBilling.discount - data.newBilling.price, 2)}
                    </strong>
                  </td>
                </tr>
              )}
              <tr>
                <td className="px-8 w-1/2 align-top py-2">
                  <strong>Prorated Total</strong>
                </td>
                <td className="align-top py-2"> =</td>
                <td className="w-1/2 align-top py-2">
                  <strong>LKR {Utils.formatNumber(data.newBilling.amount, 2)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          <table>
            <tbody>
              <tr>
                <td className="w-1/2 px-8 font-bold align-top py-2">Billing Period</td>
                <td className="align-top py-2">:</td>
                <td className="w-1/2 align-top py-2">
                  {data.newBilling.cycle}
                  {data.newBilling.additionalDays > 0 && (
                    <>
                      <br />
                      <span className="text-red-600">*</span> Extended billing period by{' '}
                      {data.newBilling.additionalDays} days for remaining amount
                    </>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="text-center text-red-600 py-4">
            After this billing period, you will automatically be charged the{' '}
            {data.nextPackage.fullName} cost.
          </div>
        </>
      ) : (
        <>
          <div className="w-full px-10">
            <div>
              <strong>New Billing Amount</strong>
            </div>
            <div className="pl-4">
              LKR {Utils.formatNumber(data.newBilling.price, 2)} (Billed {data.nextPackage.billing})
            </div>
          </div>
          <div className="w-full px-10 mt-4">
            <div>
              <strong>Billing Period</strong>
            </div>
            <div className="pl-4">{data.newBilling.cycle}</div>
          </div>
        </>
      )}
    </div>
  );
};

const BillingCycleChange: FC<{ data: SubscriptionData }> = ({ data }) => {
  return (
    <div className="flex flex-col items-center">
      <p className="text-center">
        {`You're about to change your billing cycle from `}
        <strong>{data.currentPackage.fullName}</strong> to{' '}
        <strong>{data.nextPackage.fullName}</strong>
      </p>
      <p className="text-center">
        Your billing cycle will be changed and will be charged with the total amount of the selected
        subscription plan for the new billing period at the beginning of the next billing cycle
      </p>
      <div className="w-full px-10">
        <div>
          <strong>New Billing Amount</strong>
        </div>
        <div className="pl-4">
          LKR {Utils.formatNumber(data.newBilling.amount, 2)} (Billed {data.nextPackage.billing})
        </div>
      </div>
      <div className="w-full px-10 mt-4">
        <div>
          <strong>Billing Period</strong>
        </div>
        <div className="pl-4">{data.newBilling.cycle}</div>
      </div>
    </div>
  );
};

const SubscriptionChangeModal: ForwardRefRenderFunction<SubscriptionChangeModalInstance, any> = (
  props,
  ref,
) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [data, setData] = useState<SubscriptionData>();
  const [changeType, setChangeType] = useState<string>('NONE');
  const recruiter = useRecruiter();
  const connection = useConnection();
  useImperativeHandle(
    ref,
    () => ({
      show: (subscriptionId: string) => {
        console.log('Changing', subscriptionId);
        setVisible(true);
        setChangeType('NONE');
        setLoading(true);
        setData(undefined);
        connection
          .get(`subscription/${recruiter.recruiterId}/change-info/${subscriptionId}`)
          .then((value) => {
            if (value.status === 'NO_CHANGE') {
              Modal.success({
                title: 'Already subscribed',
                content: 'You are already subscribed for this package.',
                onOk: () => setVisible(false),
              });
            } else if (value.status === 'FREE_TRIAL') {
              Modal.success({
                title: 'Not allowed',
                content: 'You are not allowed to subscribe this package.',
                onOk: () => setVisible(false),
              });
            } else {
              setChangeType(value.status);
              setData(value);
            }
          })
          .finally(() => setLoading(false));
      },
    }),
    [connection, recruiter?.recruiterId],
  );
  const title =
    changeType === 'SAME'
      ? 'Change Billing Cycle'
      : changeType === 'UP'
      ? 'Upgrade Subscription Plan'
      : changeType === 'DOWN'
      ? 'Downgrade Subscription Plan'
      : changeType === 'AUTO'
      ? 'Change Subscription Plan'
      : '';

  const successMessage = useMemo(() => {
    if (!data) {
      return 'You have successfully changed your subscription plan';
    }
    if (changeType === 'DOWN') {
      return `You have successfully changed your subscription plan from ${data.currentPackage.fullName} to ${data.nextPackage.fullName}`;
    }
    if (changeType === 'UP') {
      return `You have successfully changed your subscription plan from ${data.currentPackage.fullName} to ${data.nextPackage.fullName}`;
    }
    if (changeType === 'SAME') {
      return `Your new billing cycle change from ${data.currentPackage.billing} to ${data.nextPackage.billing} will be activated at the end of the current billing cycle.`;
    }
    return '';
  }, [changeType, data]);
  // const okText =
  //   changeType === 'SAME'
  //     ? 'Change'
  //     : changeType === 'UP'
  //     ? 'Upgrade'
  //     : changeType === 'DOWN'
  //     ? 'Change'
  //     : changeType === 'AUTO'
  //     ? 'OK'
  //     : 'OK';
  return (
    <ResponsiveModal
      width={1024}
      open={visible}
      onCancel={() => setVisible(false)}
      title={title}
      confirmLoading={submitting}
      footer={loading ? <></> : undefined}
      onOk={() => {
        if (data?.status === 'UP' && (data?.newBilling?.amount ?? 0) > 0) {
          Router.push({
            pathname: `/recruiter/${recruiter.recruiterId}/payment`,
            query: {
              message: `You have successfully changed your subscription plan from ${data.currentPackage.fullName} to ${data.nextPackage.fullName}`,
              cardMessage: 'Payment Method successfully saved.',
              monthly: data?.nextPackage.billing === 'Monthly',
              packageId: data?.nextPackage?.key,
              price: data?.newBilling.amount,
            },
          }).then();
        } else {
          setSubmitting(true);
          connection
            .post(`subscription/change`, {
              packageId: data?.nextPackage?.key,
              recruiterId: recruiter.recruiterId,
              saveCard: false,
            })
            .then((res) => {
              if (res.response?.status === 'SUCCESS') {
                Router.push({
                  pathname: `/recruiter/${recruiter.recruiterId}/subscription-dashboard`,
                  query: {
                    message: successMessage,
                  },
                }).then(() => window.location.reload());
              } else {
                Modal.error({
                  title: 'Failed',
                  content: 'Failed to change subscription. Please contact C360 support.',
                });
              }
            })
            .catch(() => {
              Modal.error({
                title: 'Failed',
                content: 'Failed to change subscription. Please contact C360 support.',
              });
            })
            .finally(() => setSubmitting(false));
        }
      }}
      okText={'Confirm'}>
      <Spin spinning={loading}>
        {data ? (
          <div>
            {changeType === 'AUTO' && <NoAction data={data} />}
            {changeType === 'SAME' && <BillingCycleChange data={data} />}
            {changeType === 'DOWN' && <DowngradeSubscription data={data} />}
            {changeType === 'UP' && <UpgradeSubscription data={data} />}
          </div>
        ) : (
          <div className="h-[200px]" />
        )}
      </Spin>
    </ResponsiveModal>
  );
};

export const useSubscriptionChangeModal = () => useRef<SubscriptionChangeModalInstance>(null);

export default React.forwardRef<SubscriptionChangeModalInstance, any>(SubscriptionChangeModal);
